import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Col, Form, Row } from 'antd';

import { convertFromUTCToEST } from '@/utils/date';
import { generateUUIDv4 } from '@/utils/number';
import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';

import { LD_CLAIM_DUPLICATE_STATUS, LD_CLAIM_DUPLICATE_STATUS_MESSAGE } from '../../../../contants/ClaimsEnums';
import { setAppLoader, setPageBlocker } from '../../../../redux/common/CommonActions';
import { valueToNumber, valueToString } from '../../../../utilz';
import { routeFileSelector } from '../../../../utilz/i18n';
import { convertPreClaimsFilesToAPI } from '../../../../utilz/PreClaimsUtils';
import FormLabel from '../../../components/Labels/FormLabel';
import SecondaryLabel from '../../../components/Labels/SecondaryLabel';
import AlertModal from '../../../components/Modals/AlertModal';
import ConfirmationModal from '../../../components/Modals/ConfirmationModal';
import Paper from '../../../components/Paper/Paper';
import ContentSpinner from '../../../components/Spinners/ContentSpinner';
import SaveCancelButtons from '../../Shipment/components/SaveCancelButtons';
import { CreatePreClaimsTab } from './components';
import { PRE_CLAIMS_CREATE_DEFAULT_VALUES } from './constants';
import { checkDuplicatePreClaim } from './data-provider/check-duplicate-pre-claim';
import { useCreatePreClaim, useGetPreClaimsInitialValues } from './hooks';

const CreatePreClaimsScreen = () => {
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const invoiceAndFbNoRef = useRef({
    claimProNo: undefined,
    invoiceNo: undefined,
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const changeCompany = useSelector((state) => state.auth.changeCompany);
  const [activeKey, setActiveKey] = useState(['1', '2', '3', '4']);
  const [conformationModal, setConformationModal] = useState(false);

  const [itemList, setItemList] = useState(PRE_CLAIMS_CREATE_DEFAULT_VALUES.ITEMS.map((i) => ({ ...i })));
  const [notesList, setNotesList] = useState(
    PRE_CLAIMS_CREATE_DEFAULT_VALUES.NOTES.map((i) => ({
      ...i,
      id: generateUUIDv4(),
    })),
  );
  const [filesList, setFilesList] = useState(
    PRE_CLAIMS_CREATE_DEFAULT_VALUES.FILES.map((i) => ({
      ...i,
      id: generateUUIDv4(),
    })),
  );

  const resetLists = () => {
    setItemList(PRE_CLAIMS_CREATE_DEFAULT_VALUES.ITEMS.map((i) => ({ ...i })));

    setNotesList(
      PRE_CLAIMS_CREATE_DEFAULT_VALUES.NOTES.map((i) => ({
        ...i,
        id: generateUUIDv4(),
      })),
    );

    setFilesList(
      PRE_CLAIMS_CREATE_DEFAULT_VALUES.FILES.map((i) => ({
        ...i,
        id: generateUUIDv4(),
      })),
    );
  };
  const [submitForm] = Form.useForm();
  const [detailsForm] = Form.useForm();
  const [isInstantDuplicateValidation, setIsInstantDuplicateValidation] = useState(false);
  const [preClaimCreatedNumber, setPreClaimCreatedNumber] = useState(false);
  const isInitialRenderRef = useRef(true);
  const { selectedCompany } = useSelector((state) => state.auth);

  const { data: initialValues, isFetching: isInitialValuesLoading } = useGetPreClaimsInitialValues(
    selectedCompany?.companyId,
  );

  const { mutate: createPreClaim } = useCreatePreClaim();

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO: fix
  useEffect(() => {
    if (changeCompany.success && !isInitialRenderRef.current) {
      resetLists();
      submitForm.resetFields();
      detailsForm.resetFields();
    }
    isInitialRenderRef.current = false;
  }, [changeCompany]);

  const handleFormProviderSubmit = (form) => {
    if (form === 'saveButtonForm') {
      initiateSubmitProcess();
    }
  };

  const initiateSubmitProcess = async (isInstantValidation, changedFieldName) => {
    try {
      if (!isInstantValidation) {
        dispatch(setAppLoader(true));
        await detailsForm.validateFields();
      }
      const basicDetails = detailsForm.getFieldsValue();

      if (isInstantValidation && changedFieldName) {
        const currentValue = invoiceAndFbNoRef.current[changedFieldName] || '';
        const newValue = basicDetails[changedFieldName] || '';
        if (currentValue === newValue) {
          return;
        }
      }

      invoiceAndFbNoRef.current = {
        claimProNo: basicDetails.claimProNo,
        invoiceNo: basicDetails.invoiceNo,
      };

      const itemListL = itemList?.filter(({ isNew }) => !isNew);
      if (itemListL?.length === 0 && !isInstantValidation) {
        showNotification({
          message: 'Minimum one claim item is required.',
          type: NOTIFICATION_TYPES.ERROR,
        });
        return false;
      }

      const payload = {
        claimKey: 0,
        freightBillNo: valueToString(basicDetails?.claimProNo),
        invoiceNo: valueToString(basicDetails?.invoiceNo),
      };

      const duplicateStatusCode = await checkDuplicatePreClaim(payload);

      if (Number(duplicateStatusCode) === LD_CLAIM_DUPLICATE_STATUS.NEW) {
        if (!isInstantValidation) {
          createClaim(payload);
        }
        return;
      }
      if (LD_CLAIM_DUPLICATE_STATUS_MESSAGE[duplicateStatusCode]) {
        if (isInstantValidation) {
          setIsInstantDuplicateValidation(true);
        }
        setConformationModal(LD_CLAIM_DUPLICATE_STATUS_MESSAGE[duplicateStatusCode]);
      }
    } catch {
      showNotification({
        message: 'Some of the fields having error,  Kindly validate it Again.',
        type: NOTIFICATION_TYPES.ERROR,
      });
    } finally {
      dispatch(setAppLoader(false));
    }
  };

  const handleClaimCreationDone = (claimKey) => {
    dispatch(setPageBlocker(false));
    setPreClaimCreatedNumber(claimKey);
  };

  const createClaim = async () => {
    try {
      await detailsForm.validateFields();
      const basicDetails = detailsForm.getFieldsValue();
      const payload = {
        branchKey: valueToNumber(basicDetails?.branchKey),
        cargoShipmentWeight: valueToNumber(basicDetails?.cargoShipmentWeight),
        carrierKey: valueToNumber(basicDetails?.carrierKey),
        claimComment: [],
        claimFile: [],
        claimItem: [],
        claimKey: 0,
        claimNoOfPieces: valueToNumber(basicDetails?.claimNoOfPieces),
        claimNumber: '',
        claimProNo: valueToString(basicDetails?.claimProNo),
        claimStatusCode: valueToString(basicDetails?.claimStatusCode),
        claimTypeKey: valueToNumber(basicDetails?.claimTypeKey),
        consigneeAddr1: valueToString(basicDetails?.consigneeAddr1),
        consigneeAddr2: valueToString(basicDetails?.consigneeAddr2),
        consigneeCity: valueToString(basicDetails?.consigneeCity),
        consigneeCountry: valueToString(basicDetails?.consigneeCountry),
        consigneeKey: valueToNumber(basicDetails?.consigneeKey),
        consigneeName: valueToString(basicDetails?.consigneeName),
        consigneeShortName: valueToString(basicDetails?.consigneeShortName),
        consigneeState: valueToString(basicDetails?.consigneeState),
        consigneeZip: valueToString(basicDetails?.consigneeZip),
        currencyCode: valueToString(basicDetails?.currencyCode),
        dateShipped: convertFromUTCToEST(basicDetails?.dateShipped),
        invoiceNo: valueToString(basicDetails?.invoiceNo),
        originKey: valueToNumber(basicDetails?.originKey),
        reasonCode: valueToString(basicDetails?.reasonCode),
        reasonType: valueToString(basicDetails?.reasonType),
        shipperAddr1: valueToString(basicDetails?.shipperAddr1),
        shipperAddr2: valueToString(basicDetails?.shipperAddr2),
        shipperCity: valueToString(basicDetails?.shipperCity),
        shipperCountry: valueToString(basicDetails?.shipperCountry),
        shipperName: valueToString(basicDetails?.shipperName),
        shipperShortName: valueToString(basicDetails?.shipperShortName),
        shipperState: valueToString(basicDetails?.shipperState),
        shipperZip: valueToString(basicDetails?.shipperZip),
      };
      const itemListL = itemList?.filter(({ isNew }) => !isNew);
      if (itemListL?.length === 0) {
        showNotification({
          message: 'Minimum one claim item is required.',
          type: NOTIFICATION_TYPES.ERROR,
        });
        return false;
      }
      payload.claimItem = itemListL.map((row) => ({
        claimantValue: valueToNumber(row.claimantValue),
        claimKey: 0,
        dirtyFlag: 'i',
        dispositionMethod: valueToString(row.dispositionMethod),
        fullRelFlag: valueToString(row.fullRelFlag),
        itemCode: valueToString(row.itemCode),
        itemDesc: valueToString(row.itemDesc),
        itemQuantity: valueToNumber(row.itemQuantity),
        itemSeq: valueToNumber(row.itemSeq),
        itemUnitMeasure: valueToString(row.itemUnitMeasure),
        itemUnitWeight: valueToNumber(row.itemUnitWeight),
        smAmount: valueToNumber(row.smAmount),
        smFlag: valueToString(row.smFlag),
        smType: valueToString(row.smType),
        uomDesc: valueToString(row.uomDesc),
        value: valueToNumber(row.value),
      }));
      const notesListL = notesList?.filter(({ isNew }) => !isNew);
      if (notesListL?.length > 0) {
        payload.claimComment = notesListL.map((row) => ({
          claimKey: 0,
          commentText: valueToString(row.commentText),
          commentType: valueToString(row.commentType),
          dirtyFlag: 'i',
          oldCommentType: '',
          sequenceNo: valueToNumber(row.sequenceNo),
        }));
      }
      const filesListL = filesList?.filter(({ isNew }) => !isNew);
      if (filesListL?.length > 0) {
        payload.claimFile = convertPreClaimsFilesToAPI(filesListL);
      }

      createPreClaim({ callback: handleClaimCreationDone, payload });
    } catch {
      showNotification({
        message: 'Some of the fields having error,  Kindly validate it Again.',
        type: NOTIFICATION_TYPES.ERROR,
      });
    }
  };

  const handleDuplicateValidationCancel = () => {
    setIsInstantDuplicateValidation(false);
    setConformationModal(false);
  };

  const handleDuplicateValidationApprove = () => {
    if (!isInstantDuplicateValidation) {
      createClaim();
    }
    handleDuplicateValidationCancel();
  };

  const handleClaimCreationConfirmationAlertClose = () => {
    detailsForm.setFieldsValue(initialValues || {});
    resetLists();
    setPreClaimCreatedNumber(false);
    invoiceAndFbNoRef.current = {
      claimProNo: undefined,
      invoiceNo: undefined,
    };
  };

  const onFieldsChange = () => {
    dispatch(setPageBlocker(true));
  };

  return (
    <>
      <Form.Provider className="overflow-hidden h-100" onFormFinish={handleFormProviderSubmit}>
        <Row className="flex-direction-column flex-nowrap overflow-hidden h-100">
          <Col className="mb-15">
            <Paper className="px-15 py-10 border-radius-bottom-none">
              <Row align="middle" justify="space-between">
                <Col>
                  <Form name="submitForm" form={submitForm}>
                    <Row gutter={15}>
                      <Col>
                        <FormLabel>Claim No : </FormLabel>
                        <SecondaryLabel className="f-14 fw-600">0</SecondaryLabel>
                      </Col>
                      <Col>
                        <FormLabel>Currency : </FormLabel>
                        <SecondaryLabel className="f-14 fw-600">{initialValues?.currencyCode}</SecondaryLabel>
                      </Col>
                    </Row>
                  </Form>
                </Col>
                <Col>
                  <Row align="middle" gutter={15}>
                    <Col>
                      <Form name="saveButtonForm">
                        <SaveCancelButtons
                          saveButtonText={t('translation:default.buttons.save')}
                          isNew
                          onCancel={() => navigate(-1)}
                          idSuffix="shipment"
                        />
                      </Form>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Paper>
          </Col>
          <Col className="flex-1 custom-scroll-bar">
            {isInitialValuesLoading ? (
              <ContentSpinner size="large" />
            ) : (
              <CreatePreClaimsTab
                activeKey={activeKey}
                setActiveKey={setActiveKey}
                detailsForm={detailsForm}
                notesList={notesList}
                setNotesList={setNotesList}
                filesList={filesList}
                setFilesList={setFilesList}
                itemList={itemList}
                setItemList={setItemList}
                initiateSubmitProcess={initiateSubmitProcess}
                onFieldsChange={onFieldsChange}
                initialValues={initialValues}
              />
            )}
          </Col>
        </Row>
      </Form.Provider>
      <ConfirmationModal
        onOk={handleDuplicateValidationApprove}
        onCancel={handleDuplicateValidationCancel}
        onClose={handleDuplicateValidationCancel}
        open={Boolean(conformationModal)}
        cancelButton={isInstantDuplicateValidation}
        description={conformationModal}
        title="Claim Duplicates"
        okText={t('translation:purchase-order-view.confirmationModal.okText')}
      />
      <AlertModal
        open={Boolean(preClaimCreatedNumber)}
        title={t('translation:claims.preClaims')}
        description={t('translation:claims.preClaimCreatedSuccessMessage', {
          claimNo: preClaimCreatedNumber,
        })}
        onClose={handleClaimCreationConfirmationAlertClose}
        onOk={handleClaimCreationConfirmationAlertClose}
      />
    </>
  );
};

export default CreatePreClaimsScreen;
